// Phase K — Risk Score — v2 MUI (LDCP-160).
//
// Four tabs: Thresholds & verdicts / Signal weights / Route overrides / Investigate.
//
// Backend wiring preserved verbatim:
//   asec.riskConfigGet / Upsert / Delete / Distribution
//   asec.riskConfigLookup(siteUid, requestId)
//   asec.riskWeightOverridesList / Set / Delete
//   asec.riskConfigRoutesList / Create / Patch / Delete

import React from "react";
import {
  Stack, Box, Typography, Tabs, Tab, Grid, Select, MenuItem, Modal, Slide,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, InputAdornment, InputLabel, LinearProgress, Alert, Chip,
} from "@mui/material";
import { useSearchParams } from "react-router-dom";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import {
  Gauge, Power, Activity, Shield, ShieldCheck, ShieldX, ShieldAlert,
  Eye, BarChart3, Scale, Route, SearchCheck, Search, GitBranch,
  Plus, Minus, RotateCcw, Check, X, FileJson,
  ListChecks, Pencil, Trash2, GripVertical, Hash, KeyRound, FileSignature,
  Waypoints, BrainCircuit, Bot, ScanEye, Lock, MapPin, ChevronUp, ChevronDown,
} from "lucide-react";
import { ReactComponent as CloseIcon } from "../../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import { ReactComponent as InfoIcon } from "../../../../../vendor/_new/icon/sites/info.svg";
import { ReactComponent as ArrowdownIcon } from "../../../../../vendor/_new/icon/sites/arrow_down.svg";

import asec from "../../../../../utils/axios/v1/asecAxios";
import { useNotification } from "../../../../../contexts/NotificationContext";
import colors from "../../../../../utils/colors";
import LayerdIcon from "../../../../common/LayerdIcon";
import FormHelpTooltip from "../../../../common/FormHelpTooltip";
import StickySaveBar from "../../../../common/StickySaveBar";
import { collectObjectDiff, enabledLabel } from "../../../../common/stickySaveBarUtils";
import { PrimaryButton, GhostButton, SaveButton, CancelButton, formSaveButtonSx, formCancelButtonSx } from "../../../../common/Button";
import { GreenSwitch, SafeTextField, GreenSelectForm } from "../../../../common/Greens";
import AsecPage from "./AsecPage";
import useAsecPermissions from "./useAsecPermissions";
import {
  asecTabsSx,
  asecSelectSx,
  asecSettingsTableHeadSx,
  asecSettingsTableBodySx,
  asecIconButtonSx,
  asecFormPrimaryButtonSx,
  asecHelperTextSx,
  AsecTextField, AsecField,
} from "./asecUi";
import {
  AsecMetricStrip,
  AsecSectionCard,
  AsecSettingsTableCard,
  AsecMono,
  AsecStatusPill,
  AsecHeroStatValue,
} from "./components/asecPrimitives";

const TAB_KEYS = ["thresholds", "weights", "routes", "investigate"];
const DEFAULT_TAB = "thresholds";

function resolveRiskScoreTab(raw) {
  if (raw == null) return DEFAULT_TAB;
  const key = String(raw).trim();
  if (!key) return DEFAULT_TAB;
  return TAB_KEYS.includes(key) ? key : DEFAULT_TAB;
}

const RISK_POLICY_DIFF_FIELDS = [
  { key: "inline_enforce", label: "Inline enforce", format: enabledLabel },
  { key: "challenge_threshold", label: "Challenge threshold" },
  { key: "deny_threshold", label: "Deny threshold" },
  {
    key: "challenge_response_format",
    label: "Challenge response format",
    format: (v) => ({
      auto: "auto — match Accept",
      json: "json — problem+json",
      html: "html — challenge page",
    }[v] || String(v ?? "—")),
  },
];
const ROUTE_METHODS = ["ANY", "GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"];
const SIGNAL_META = {
  bola_denied:     { sev: "crit", Icon: ShieldX,       desc: "Ownership check denied (BOLA/IDOR)" },
  signature:       { sev: "crit", Icon: FileSignature, desc: "HMAC / request signature invalid" },
  api_anomaly:     { sev: "high", Icon: Waypoints,      desc: "Endpoint sequence anomaly" },
  ml_high:         { sev: "high", Icon: BrainCircuit,  desc: "ML model high-confidence threat" },
  auth_invalid:    { sev: "high", Icon: KeyRound,      desc: "Token present but failed validation" },
  bot_flagged:     { sev: "high", Icon: Bot,            desc: "Automated client fingerprint" },
  dlp_high:        { sev: "med",  Icon: ScanEye,       desc: "High-sensitivity data in payload" },
  tls_deprecated:  { sev: "med",  Icon: Lock,           desc: "Deprecated TLS version / cipher" },
  ml_med:          { sev: "med",  Icon: BrainCircuit,  desc: "ML model medium-confidence" },
  auth_missing:    { sev: "med",  Icon: KeyRound,      desc: "No credential on a protected route" },
  geo_unusual:     { sev: "low",  Icon: MapPin,        desc: "Request from atypical geography" },
  dlp_medium:      { sev: "low",  Icon: ScanEye,       desc: "Medium-sensitivity data in payload" },
  rate_burst:      { sev: "low",  Icon: Gauge,          desc: "Short-window request burst" },
  prompt_injection:{ sev: "high", Icon: BrainCircuit,  desc: "Prompt-injection signature in payload" },
};

function sigPalette(sev) {
  return sev === "crit" ? { ic: colors.red[200], icBg: colors.red[10], bar: colors.red[100], end: colors.red[200] }
       : sev === "high" ? { ic: "#B25A1A", icBg: "#FFF1E6", bar: "#F2882B", end: "#C2641A" }
       : sev === "med"  ? { ic: "#7a5b10", icBg: colors.yellow[10], bar: colors.yellow[100], end: "#C29021" }
       : sev === "low"  ? { ic: colors.primary[400], icBg: colors.primary[10], bar: colors.primary[300], end: colors.primary[500] }
       : { ic: colors.neutral[700], icBg: colors.neutral[50], bar: colors.primary[300], end: colors.primary[500] };
}

function emptyRoute() {
  return { method: "GET", path_template: "/v1/orders/{orderId}",
    inline_enforce: true, challenge_threshold: 30, deny_threshold: 50 };
}

function synthesiseHistogram(dist, thrCh, thrDn) {
  const buckets = new Array(25).fill(0);
  const total = dist.reduce((a, r) => a + Number(r.rows_24h || 0), 0) || 1;
  const allowCount = Number(dist.find((r) => r.verdict === "allow")?.rows_24h || 0);
  const chCount    = Number(dist.find((r) => r.verdict === "challenge")?.rows_24h || 0);
  const dnCount    = Number(dist.find((r) => r.verdict === "deny")?.rows_24h || 0);
  const fillBand = (lo, hi, count) => {
    const loB = Math.max(0, Math.floor(lo / 4));
    const hiB = Math.min(24, Math.ceil(hi / 4));
    if (hiB < loB || count === 0) return;
    let acc = 0;
    const weights = [];
    for (let i = loB; i <= hiB; i += 1) {
      const bucketCentre = (i + 0.5) * 4;
      const mid = (lo + hi) / 2;
      const w = Math.exp(-Math.pow((bucketCentre - mid) / Math.max(8, (hi - lo) / 2), 2));
      weights.push(w);
      acc += w;
    }
    for (let i = loB; i <= hiB; i += 1) buckets[i] += count * weights[i - loB] / acc;
  };
  fillBand(0, thrCh - 1, allowCount);
  fillBand(thrCh, thrDn - 1, chCount);
  fillBand(thrDn, 100, dnCount);
  return { buckets, total, allowCount, chCount, dnCount };
}

function Verdict({ v, sm }) {
  const palette = v === "allow"    ? { bg: colors.green[300], fg: "#fff", Icon: Check }
                : v === "challenge"? { bg: colors.yellow[100], fg: "#3d2e08", Icon: ShieldAlert }
                : v === "deny"     ? { bg: colors.red[200], fg: "#fff", Icon: X }
                : { bg: colors.neutral[50], fg: colors.neutral[700], Icon: Shield };
  return (
    <Stack direction="row" alignItems="center" spacing={0.875} sx={{
      height: sm ? 22 : 28, px: sm ? 1.125 : 1.625, borderRadius: "999px",
      fontSize: sm ? 11 : 12.5, fontWeight: 700,       backgroundColor: palette.bg, color: palette.fg, width: "fit-content",
    }}>
      <LayerdIcon icon={palette.Icon} size="sm" color="inherit" />
      {v || "—"}
    </Stack>
  );
}

function MethodTag({ m }) {
  const palette = m === "GET" ? { bg: colors.green[10], fg: colors.green[300], bd: "rgba(31,193,107,0.30)" }
                : m === "POST" ? { bg: colors.primary[10], fg: colors.primary[400], bd: colors.primary[200] }
                : m === "PUT" || m === "PATCH" ? { bg: colors.yellow[10], fg: "#7a5b10", bd: "rgba(223,180,0,0.40)" }
                : m === "DELETE" ? { bg: colors.red[10], fg: colors.red[200], bd: "rgba(251,55,72,0.30)" }
                : { bg: colors.neutral[50], fg: colors.neutral[700], bd: colors.neutral[200] };
  return (
    <Box sx={{
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      minWidth: 54, height: 24, px: 1.125, borderRadius: "6px",
      fontSize: 12, fontWeight: 700,
      backgroundColor: palette.bg, color: palette.fg, border: `1px solid ${palette.bd}`,
    }}>
      {m}
    </Box>
  );
}

function PathCell({ template }) {
  if (!template) return <Typography sx={{ color: colors.neutral[500] }}>—</Typography>;
  const parts = String(template).split("/");
  const nodes = [];
  parts.forEach((p, i) => {
    if (i === 0 && p === "") return;
    nodes.push(<Box key={`s${i}`} component="span" sx={{ color: colors.neutral[500] }}>/</Box>);
    if (p.startsWith("{") && p.endsWith("}")) {
      nodes.push(
        <Box key={`p${i}`} component="span" sx={{
          color: colors.primary[300], backgroundColor: "rgba(99,113,247,0.10)",
          px: 0.625, borderRadius: "4px", fontWeight: 600,
        }}>{p}</Box>);
    } else {
      nodes.push(<Box key={`g${i}`} component="span">{p}</Box>);
    }
  });
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, fontSize: 12, flexWrap: "wrap" }}>
      {nodes}
    </Box>
  );
}

function HeroMetrics({ form, dist }) {
  const total = dist.reduce((a, r) => a + Number(r.rows_24h || 0), 0);
  const chRow = dist.find((r) => r.verdict === "challenge");
  const dnRow = dist.find((r) => r.verdict === "deny");
  const allowRow = dist.find((r) => r.verdict === "allow");
  const chCount = Number(chRow?.rows_24h || 0);
  const dnCount = Number(dnRow?.rows_24h || 0);
  const p95 = Math.max(...dist.map((r) => Number(r.p95 || 0)), 0);
  const pct = (n) => (total > 0 ? (n / total * 100).toFixed(2) : "0.00");

  return (
    <AsecMetricStrip metrics={[
      {
        key: "enforcement",
        label: "Enforcement",
        icon: Power,
        highlight: true,
        value: (
          <AsecHeroStatValue
            primary={form.inline_enforce ? "On" : "Off"}
            pill={form.inline_enforce
              ? <AsecStatusPill kind="ok">Enforcing inline</AsecStatusPill>
              : <AsecStatusPill kind="warn">Observing only</AsecStatusPill>}
          />
        ),
        sub: `Verdicts ${form.inline_enforce ? "applied inline" : "computed but not applied"} · challenge ≥ ${form.challenge_threshold} · deny ≥ ${form.deny_threshold}`,
      },
      {
        key: "scored",
        label: "Scored · 24h",
        icon: Activity,
        value: total.toLocaleString(),
        sub: total > 0 ? "live count" : "no traffic yet",
      },
      {
        key: "challenge",
        label: "Would challenge",
        icon: ShieldAlert,
        value: chCount.toLocaleString(),
        valueSx: { color: "#C29021" },
        sub: `${pct(chCount)}% · score ${form.challenge_threshold}–${form.deny_threshold - 1}`,
      },
      {
        key: "deny",
        label: "Would deny",
        icon: ShieldX,
        value: dnCount.toLocaleString(),
        valueSx: { color: colors.red[200] },
        sub: `${pct(dnCount)}% · score ≥ ${form.deny_threshold}`,
      },
      {
        key: "p95",
        label: "Score p95",
        icon: BarChart3,
        value: <AsecHeroStatValue primary={Math.round(p95)} secondary="/ 100" />,
        sub: `p50 ${Math.round(Number(allowRow?.p50 || 0))} · max ${Math.round(Math.max(...dist.map((r) => Number(r.max || 0)), 0))}`,
      },
    ]} />
  );
}

function Histogram({ buckets, thrCh, thrDn }) {
  const max = Math.max(...buckets, 1);
  return (
    <Box sx={{ position: "relative", mt: 4.25 }}>
      {[
        { left: `${thrCh}%`, label: `challenge ${thrCh}`, kind: "warn" },
        { left: `${thrDn}%`, label: `deny ${thrDn}`, kind: "danger" },
      ].map((m) => (
        <Box key={m.label} sx={{
          position: "absolute", top: -26, transform: "translateX(-50%)", left: m.left,
          fontSize: 12, fontWeight: 700, px: 1, py: 0.25,
          borderRadius: "6px", whiteSpace: "nowrap", zIndex: 3,
          display: "flex", alignItems: "center", gap: 0.625,
          backgroundColor: m.kind === "warn" ? "#FFF6D0" : "#FEECEE",
          color: m.kind === "warn" ? "#7a5b10" : colors.red[200],
          border: `1px solid ${m.kind === "warn" ? "#FFE89A" : "#FBC9CF"}`,
        }}>
          <LayerdIcon icon={GripVertical} size="sm" color="inherit" style={{ opacity: 0.6 }} />
          {m.label}
        </Box>
      ))}
      <Box sx={{ position: "absolute", top: 0, height: 150, left: `${thrCh}%`, borderLeft: "2px dashed #C29021", zIndex: 2 }} />
      <Box sx={{ position: "absolute", top: 0, height: 150, left: `${thrDn}%`, borderLeft: `2px dashed ${colors.red[200]}`, zIndex: 2 }} />
      <Box sx={{
        position: "relative", height: 150, display: "flex", alignItems: "flex-end",
        gap: 0.25, px: 0.25, borderBottom: `1.5px solid ${colors.neutral[400]}`,
      }}>
        {buckets.map((v, i) => {
          const score = (i + 0.5) * 4;
          const bg = score >= thrDn ? "#E8485A" : score >= thrCh ? colors.yellow[100] : "#3CC373";
          return (
            <Box key={i} sx={{
              flex: 1, borderRadius: "3px 3px 0 0", minHeight: 2,
              height: `${Math.max(1.5, (v / max) * 100)}%`, backgroundColor: bg,
            }} />
          );
        })}
      </Box>
      <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.875, fontSize: 12, color: colors.neutral[500], }}>
        {["0", "20", "40", "60", "80", "100"].map((n) => <span key={n}>{n}</span>)}
      </Stack>
    </Box>
  );
}

function ThresholdStepper({ value, onChange, min = 0, max = 100, step = 1, disabled }) {
  return (
    <Stack direction="row" alignItems="center" sx={{
      border: `1px solid ${colors.neutral[200]}`, borderRadius: "9px", overflow: "hidden",
      height: 40, width: 148, backgroundColor: "#fff",
    }}>
      <IconButton disabled={disabled} onClick={() => onChange(Math.max(min, value - step))}
        sx={{
          width: 38, height: "100%", borderRadius: 0, flexShrink: 0,
          backgroundColor: colors.neutral[50],
          "&:hover": { backgroundColor: colors.neutral[100] },
        }}>
        <LayerdIcon icon={Minus} size="sm" color="neutral" />
      </IconButton>
      <SafeTextField
        type="number"
        disabled={disabled}
        value={value}
        onChange={(e) => {
          const v = parseInt(e.target.value, 10);
          if (Number.isNaN(v)) return;
          onChange(Math.min(max, Math.max(min, v)));
        }}
        variant="standard"
        InputProps={{
          disableUnderline: true,
          sx: {
            height: "100%",
            "& input": {
              textAlign: "center",
              fontSize: 16,
              fontWeight: 700,
              lineHeight: "40px",
              py: 0,
              px: 0,
              height: 40,
              boxSizing: "border-box",
              // Hide browser number spinners so the value sits visually centered.
              MozAppearance: "textfield",
              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
            },
          },
        }}
        inputProps={{
          inputMode: "numeric",
          "aria-label": "Threshold value",
        }}
        sx={{
          flex: 1,
          height: "100%",
          "& .MuiInputBase-root": { height: "100%" },
          "& .MuiInput-root": { marginTop: 0 },
        }}
      />
      <IconButton disabled={disabled} onClick={() => onChange(Math.min(max, value + step))}
        sx={{
          width: 38, height: "100%", borderRadius: 0, flexShrink: 0,
          backgroundColor: colors.neutral[50],
          "&:hover": { backgroundColor: colors.neutral[100] },
        }}>
        <LayerdIcon icon={Plus} size="sm" color="neutral" />
      </IconButton>
    </Stack>
  );
}

function ZoneLabel({ kind, range, count, pct: pctVal, flex }) {
  const palette = kind === "allow"     ? { bg: "#F4FBF6", bd: "rgba(31,193,107,0.30)", fg: colors.green[300] }
                : kind === "challenge" ? { bg: "#FFFCEF", bd: "#FFE89A", fg: "#7a5b10" }
                : { bg: "#FFF7F8", bd: "#FBC9CF", fg: colors.red[200] };
  return (
    <Box sx={{
      flex, p: 1.25, borderRadius: "9px", border: `1px solid ${palette.bd}`,
      backgroundColor: palette.bg, display: "flex", flexDirection: "column", gap: 0.25,
    }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", color: palette.fg }}>{kind}</Typography>
      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{range}</Typography>
      <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
        <strong style={{ color: palette.fg }}>{Number(count).toLocaleString()}</strong> · {pctVal}%
      </Typography>
    </Box>
  );
}

function EnforcementBanner({ on, onChange, disabled }) {
  const palette = on
    ? { bg: "linear-gradient(180deg,#F4FBF6,#EAFAF0)", bd: "rgba(31,193,107,0.30)", Icon: ShieldCheck, ic: colors.green[300] }
    : { bg: "linear-gradient(180deg,#FFFCEF,#FFF9E3)", bd: "#FFE89A", Icon: Eye, ic: "#C29021" };
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}
      sx={{ p: 2, borderRadius: "12px", border: `1px solid ${palette.bd}`, background: palette.bg, mb: 2.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.75}>
        <Box sx={{
          width: 40, height: 40, borderRadius: "11px", backgroundColor: "#fff",
          border: `1px solid ${palette.bd}`, color: palette.ic,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <LayerdIcon icon={palette.Icon} size="md" color="inherit" />
        </Box>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1.125} sx={{ fontSize: 16, fontWeight: 700 }}>
            Inline enforcement
            {on ? <AsecStatusPill kind="ok">Enforcing inline</AsecStatusPill> : <AsecStatusPill kind="warn">Observing only</AsecStatusPill>}
          </Stack>
          <Typography sx={{ fontSize: 12, color: colors.neutral[700], mt: 0.25 }}>
            {on
              ? <>Verdicts are <strong>applied inline</strong> — requests at or above the thresholds receive 429 / 403 in real time.</>
              : <>Verdicts are computed by the logging-phase scorer but <strong>not applied</strong> — flip on once the distribution looks right.</>}
          </Typography>
        </Box>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>{on ? "On" : "Off"}</Typography>
        <GreenSwitch checked={on} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      </Stack>
    </Stack>
  );
}

function LegendItem({ swatch, text }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.875} sx={{ fontSize: 12, color: colors.neutral[700] }}>
      <Box sx={{ width: 12, height: 12, borderRadius: "3px", backgroundColor: swatch }} />
      {text}
    </Stack>
  );
}

function ThrCard({ kind, label, children, helper }) {
  const sw = kind === "ch" ? colors.yellow[100] : colors.red[200];
  return (
    <Box sx={{ border: `1px solid ${colors.neutral[200]}`, borderRadius: "11px", p: 1.75, backgroundColor: "#FAFBFC" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.25 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ fontSize: 14, fontWeight: 600 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: "3px", backgroundColor: sw, flexShrink: 0 }} />
          <Box component="span">{label}</Box>
        </Stack>
        {children}
      </Stack>
      <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "25px" }}>{helper}</Typography>
    </Box>
  );
}

function ThresholdsTab({ form, patch, dist, canConfigure }) {
  const thrCh = form.challenge_threshold;
  const thrDn = form.deny_threshold;
  const hist = React.useMemo(() => synthesiseHistogram(dist, thrCh, thrDn), [dist, thrCh, thrDn]);
  const total = hist.total;
  const allowPct = total > 0 ? ((hist.allowCount / total) * 100).toFixed(1) : "0.0";
  const chPct = total > 0 ? ((hist.chCount / total) * 100).toFixed(1) : "0.0";
  const dnPct = total > 0 ? ((hist.dnCount / total) * 100).toFixed(1) : "0.0";
  const wouldDenyPct = Number(dnPct);
  const balancedTone = wouldDenyPct < 2 ? "ok" : wouldDenyPct < 5 ? "warn" : "danger";

  return (
    <>
      <EnforcementBanner on={!!form.inline_enforce} disabled={!canConfigure}
        onChange={(v) => patch({ inline_enforce: v })} />

      <Grid container spacing={2.5} alignItems="flex-start">
        <Grid item xs={12} lg={7}>
          <AsecSectionCard
            title="Score thresholds"
            icon={BarChart3}
            radius="12px"
            noPadding
            right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>24h distribution · {total.toLocaleString()} requests</Typography>}
          >
            <Box sx={{ px: 3, pt: 2.75, pb: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2.25} flexWrap="wrap" sx={{ mb: 2.5 }}>
                <LegendItem swatch="#22A85B" text={<><strong>Allow</strong> 0–{thrCh - 1}</>} />
                <LegendItem swatch={colors.yellow[100]} text={<><strong>Challenge</strong> {thrCh}–{thrDn - 1} → 429</>} />
                <LegendItem swatch={colors.red[200]} text={<><strong>Deny</strong> {thrDn}–100 → 403</>} />
              </Stack>
              <Histogram buckets={hist.buckets} thrCh={thrCh} thrDn={thrDn} />
              <Stack direction="row" spacing={0.75} sx={{ mt: 1.75 }}>
                <ZoneLabel kind="allow" range={`0 – ${thrCh - 1}`} count={hist.allowCount} pct={allowPct} flex={thrCh} />
                <ZoneLabel kind="challenge" range={`${thrCh} – ${thrDn - 1}`} count={hist.chCount} pct={chPct} flex={Math.max(1, thrDn - thrCh)} />
                <ZoneLabel kind="deny" range={`${thrDn} – 100`} count={hist.dnCount} pct={dnPct} flex={Math.max(1, 100 - thrDn + 1)} />
              </Stack>
            </Box>
            <Box sx={{ px: 2.25, pb: 2.25 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <ThrCard kind="ch" label="Challenge threshold"
                    helper={<>Score ≥ this returns <AsecMono>429</AsecMono> + <AsecMono>Retry-After</AsecMono>.</>}>
                    <ThresholdStepper value={thrCh} disabled={!canConfigure}
                      onChange={(v) => patch({ challenge_threshold: v })} />
                  </ThrCard>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <ThrCard kind="dn" label="Deny threshold"
                    helper={<>Score ≥ this returns <AsecMono>403</AsecMono> problem+json.</>}>
                    <ThresholdStepper value={thrDn} disabled={!canConfigure}
                      onChange={(v) => patch({ deny_threshold: v })} />
                  </ThrCard>
                </Grid>
              </Grid>
              <Alert severity={balancedTone === "ok" ? "info" : balancedTone === "warn" ? "warning" : "error"} icon={<LayerdIcon icon={Scale} size="sm" color="inherit" />} sx={{ mt: 1.75, fontSize: 12 }}>
                {wouldDenyPct < 2
                  ? <><strong>Balanced.</strong> Most lone signals pass; common combinations trip Challenge. Only <strong>{dnPct}%</strong> of current traffic would be denied at these thresholds.</>
                  : wouldDenyPct < 5
                    ? <><strong>Tight.</strong> <strong>{dnPct}%</strong> of traffic would be denied. Watch your false-positive rate when flipping enforcement on.</>
                    : <><strong>Aggressive.</strong> <strong>{dnPct}%</strong> of traffic would be denied. Tune up the deny threshold or signal weights first.</>}
              </Alert>
            </Box>
          </AsecSectionCard>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Stack spacing={2.5}>
            <AsecSectionCard title="Challenge response" icon={FileJson} radius="12px">
              <AsecField label="Response format">
                <Select fullWidth size="small" sx={asecSelectSx}
                  value={form.challenge_response_format || "auto"}
                  disabled={!canConfigure}
                  onChange={(e) => patch({ challenge_response_format: e.target.value })}>
                  <MenuItem value="auto">auto — match the request&apos;s Accept header</MenuItem>
                  <MenuItem value="json">json — RFC 7807 problem+json (SDK-only sites)</MenuItem>
                  <MenuItem value="html">html — branded challenge page (browser-only sites)</MenuItem>
                </Select>
              </AsecField>
            </AsecSectionCard>

            <AsecSectionCard title="24-hour verdict distribution" icon={ListChecks} radius="12px" noPadding bodySx={{ p: 0 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                      <TableCell sx={asecSettingsTableHeadSx}>Verdict</TableCell>
                      <TableCell sx={asecSettingsTableHeadSx} align="right">Count</TableCell>
                      <TableCell sx={asecSettingsTableHeadSx} align="right">p50</TableCell>
                      <TableCell sx={asecSettingsTableHeadSx} align="right">p95</TableCell>
                      <TableCell sx={asecSettingsTableHeadSx} align="right">max</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {dist.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4, color: colors.neutral[700] }}>
                          No scored requests in the last 24h.
                        </TableCell>
                      </TableRow>
                    )}
                    {dist.map((r) => {
                      const tone = r.verdict === "allow" ? colors.green[300] : r.verdict === "challenge" ? "#7a5b10" : colors.red[200];
                      return (
                        <TableRow key={r.verdict} hover>
                          <TableCell sx={asecSettingsTableBodySx}><Verdict v={r.verdict} /></TableCell>
                          <TableCell sx={asecSettingsTableBodySx} align="right">
                            <Typography sx={{ fontSize: 16, fontWeight: 700, color: tone }}>{Number(r.rows_24h || 0).toLocaleString()}</Typography>
                          </TableCell>
                          <TableCell sx={asecSettingsTableBodySx} align="right">{Math.round(Number(r.p50 || 0))}</TableCell>
                          <TableCell sx={asecSettingsTableBodySx} align="right">{Math.round(Number(r.p95 || 0))}</TableCell>
                          <TableCell sx={asecSettingsTableBodySx} align="right">{Math.round(Number(r.max || 0))}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography sx={{ px: 2.25, py: 1.375, borderTop: `1px solid ${colors.neutral[200]}`, fontSize: 12, color: colors.neutral[500] }}>
                Observational counts — computed by the logging-phase scorer. These would have driven the inline action if enforcement were on.
              </Typography>
            </AsecSectionCard>
          </Stack>
        </Grid>
      </Grid>
    </>
  );
}

function clampWeight(n) {
  return Math.max(0, Math.min(100, n));
}

function WeightOverrideField({ value, onChange, disabled, dirty, ariaLabel }) {
  const numeric = Number(value);
  const current = Number.isFinite(numeric) ? numeric : 0;

  const bump = (delta) => {
    if (disabled) return;
    onChange(String(clampWeight(Math.round(current + delta))));
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    // Cleared field → show 0 (do not leave empty).
    if (raw === "" || raw == null) {
      onChange("0");
      return;
    }
    // "04" → "4" (leading zeros on whole numbers).
    if (/^0\d+$/.test(raw)) {
      onChange(String(clampWeight(Number(raw))));
      return;
    }
    const v = Number(raw);
    if (Number.isNaN(v)) return;
    onChange(String(clampWeight(v)));
  };

  const handleBlur = () => {
    if (value === "" || value == null || isNaN(Number(value))) {
      onChange("0");
      return;
    }
    onChange(String(clampWeight(Math.round(Number(value)))));
  };

  const handleFocus = (e) => {
    // Typing over 0 replaces instead of appending (0 + 4 → 4, not 04).
    if (String(value) === "0" || Number(value) === 0) {
      e.target.select();
    }
  };

  const spinnerBtnSx = {
    p: 0,
    width: 20,
    height: 14,
    minWidth: 20,
    borderRadius: "3px",
    color: colors.neutral[700],
    "&:hover": {
      backgroundColor: colors.primary.alpha,
      color: colors.primary[300],
    },
    "&.Mui-disabled": { opacity: 0.35 },
  };

  return (
    <SafeTextField
      type="number"
      size="small"
      disabled={disabled}
      title={disabled ? "Override weight (read-only)" : "Type or use arrows to set override (0–100)"}
      value={value === "" || value == null ? "0" : value}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      inputProps={{
        min: 0,
        max: 100,
        step: 1,
        inputMode: "numeric",
        "aria-label": ariaLabel,
      }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end" sx={{ ml: 0, mr: 0.25, height: "100%", maxHeight: "none" }}>
            <Stack
              spacing={0}
              alignItems="center"
              justifyContent="center"
              sx={{
                height: 32,
                borderLeft: `1px solid ${dirty ? colors.primary[200] : colors.neutral[200]}`,
                pl: 0.5,
              }}
            >
              <IconButton
                size="small"
                disabled={disabled || current >= 100}
                aria-label="Increase override"
                onClick={() => bump(1)}
                sx={spinnerBtnSx}
              >
                <LayerdIcon icon={ChevronUp} size={12} color="inherit" />
              </IconButton>
              <IconButton
                size="small"
                disabled={disabled || current <= 0}
                aria-label="Decrease override"
                onClick={() => bump(-1)}
                sx={spinnerBtnSx}
              >
                <LayerdIcon icon={ChevronDown} size={12} color="inherit" />
              </IconButton>
            </Stack>
          </InputAdornment>
        ),
      }}
      sx={{
        width: 96,
        "& .MuiOutlinedInput-root": {
          height: 36,
          borderRadius: "8px",
          pr: 0.5,
          alignItems: "center",
          backgroundColor: disabled
            ? colors.neutral[50]
            : dirty
              ? "rgba(99,113,247,0.08)"
              : "#fff",
          "& fieldset": {
            borderWidth: "1.5px",
            borderColor: disabled
              ? colors.neutral[200]
              : dirty
                ? colors.primary[300]
                : colors.primary[200],
          },
          "&:hover": {
            backgroundColor: disabled ? colors.neutral[50] : "#fff",
            boxShadow: disabled ? "none" : `0 0 0 3px ${colors.primary.alpha}`,
            "& fieldset": {
              borderColor: disabled ? colors.neutral[200] : colors.primary[300],
            },
          },
          "&.Mui-focused": {
            backgroundColor: "#fff",
            boxShadow: `0 0 0 3px ${colors.primary.alpha}`,
            "& fieldset": {
              borderColor: colors.primary[300],
              borderWidth: "1.5px",
            },
          },
          "&.Mui-disabled": {
            backgroundColor: colors.neutral[50],
            "& fieldset": { borderColor: colors.neutral[200] },
          },
        },
        "& input.MuiOutlinedInput-input": {
          textAlign: "center",
          fontWeight: 600,
          fontSize: 14,
          lineHeight: "20px",
          color: disabled ? colors.neutral[600] : colors.neutral[1000],
          WebkitTextFillColor: disabled ? colors.neutral[600] : colors.neutral[1000],
          py: "8px",
          pl: "10px",
          pr: "4px",
          height: "auto",
          boxSizing: "border-box",
          MozAppearance: "textfield",
          "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
            WebkitAppearance: "none",
            margin: 0,
          },
        },
      }}
    />
  );
}

function WeightsTab({ defaults, overrides, drafts, setDraft, onSaveOne, onResetOne, onResetAll, canConfigure, search, setSearch }) {
  const componentList = Object.keys(defaults).sort((a, b) => defaults[b] - defaults[a]);
  const filtered = componentList.filter((c) => !search || c.toLowerCase().includes(search.toLowerCase()));
  const maxW = Math.max(...componentList.map((c) => defaults[c]), 1);

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" sx={{ mb: 1.75 }}>
        <SafeTextField
          size="small"
          placeholder="Search signal…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LayerdIcon icon={Search} size="sm" color="neutral" />
              </InputAdornment>
            ),
          }}
        />
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>
          Each signal contributes its weight when it fires. The fused score is the capped sum across all firing signals.
        </Typography>
        <Box sx={{ ml: "auto" }}>
          <GhostButton size="small" startIcon={<LayerdIcon icon={RotateCcw} size="sm" color="neutral" />}
            onClick={onResetAll} disabled={!canConfigure || Object.keys(overrides).length === 0}>
            Reset all to defaults
          </GhostButton>
        </Box>
      </Stack>

      <AsecSettingsTableCard>
        <Grid container sx={{
          px: 2.25, py: 1.375, borderBottom: `1px solid ${colors.neutral[200]}`,
          backgroundColor: colors.neutral[50], fontSize: 12, fontWeight: 700,
          textTransform: "uppercase", letterSpacing: "0.05em", color: colors.neutral[700],
        }}>
          <Grid item xs={4}>Signal</Grid>
          <Grid item xs={5}>Weight contribution</Grid>
          <Grid item xs={2} textAlign="right">Override</Grid>
          <Grid item xs={1} textAlign="right">Actions</Grid>
        </Grid>
        {filtered.length === 0 && (
          <Typography sx={{ p: 5, textAlign: "center", color: colors.neutral[700] }}>
            {componentList.length === 0 ? "Pingora hasn't reported any signals yet." : "No signals match this search."}
          </Typography>
        )}
        {filtered.map((comp) => {
          const meta = SIGNAL_META[comp] || { sev: "med", Icon: Shield, desc: "—" };
          const palette = sigPalette(meta.sev);
          const def = Number(defaults[comp] || 0);
          const ovr = overrides[comp];
          const draftV = drafts[comp];
          const cur = draftV != null ? Number(draftV) : (ovr != null ? Number(ovr) : def);
          const dirty = String(cur) !== String(ovr != null ? ovr : def);
          const overridden = ovr != null;
          const canSave = canConfigure && dirty;
          const canReset = canConfigure && (overridden || dirty);
          const { Icon: SigIcon } = meta;
          const actionBtnSx = (enabled) => ({
            ...asecIconButtonSx,
            color: enabled ? colors.primary[300] : colors.neutral[400],
            backgroundColor: enabled ? "rgba(99,113,247,0.08)" : "transparent",
            opacity: enabled ? 1 : 0.45,
            cursor: enabled ? "pointer" : "not-allowed",
            "&:hover": enabled
              ? {
                  backgroundColor: colors.primary.alpha,
                  color: colors.primary[400],
                }
              : {
                  backgroundColor: "transparent",
                  color: colors.neutral[400],
                },
            "&.Mui-disabled": {
              color: colors.neutral[400],
              opacity: 0.4,
            },
          });
          return (
            <Grid container key={comp} alignItems="center" sx={{
              px: 2.25, py: 1.5, borderBottom: `1px solid rgba(0,0,0,0.08)`,
              "&:hover": { backgroundColor: colors.neutral[50] },
            }}>
              <Grid item xs={4}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <Box sx={{
                    width: 30, height: 30, borderRadius: "8px", display: "flex",
                    alignItems: "center", justifyContent: "center", flexShrink: 0,
                    backgroundColor: palette.icBg, color: palette.ic,
                  }}>
                    <LayerdIcon icon={SigIcon} size="sm" color="inherit" />
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.75}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600 }}>{comp}</Typography>
                      {overridden && (
                        <Chip size="small" label="override" sx={{
                          height: 18, fontSize: 12, fontWeight: 700, textTransform: "uppercase",
                          color: colors.primary[300], backgroundColor: "rgba(99,113,247,0.10)",
                        }} />
                      )}
                    </Stack>
                    <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>{meta.desc}</Typography>
                  </Box>
                </Stack>
              </Grid>
              <Grid item xs={5}>
                <Stack direction="row" alignItems="center" spacing={1.25}>
                  <LinearProgress variant="determinate" value={Math.min(100, (cur / maxW) * 100)} sx={{
                    flex: 1, height: 9, borderRadius: "5px", backgroundColor: colors.neutral[50],
                    "& .MuiLinearProgress-bar": {
                      borderRadius: "5px",
                      background: `linear-gradient(90deg,${palette.bar},${palette.end})`,
                    },
                  }} />
                  <Typography sx={{ fontSize: 14, fontWeight: 700, width: 30, textAlign: "right" }}>{cur}</Typography>
                </Stack>
              </Grid>
              <Grid item xs={2} textAlign="right" sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                <WeightOverrideField
                  value={draftV != null ? draftV : (ovr != null ? ovr : def)}
                  onChange={(v) => setDraft(comp, v)}
                  disabled={!canConfigure}
                  dirty={dirty}
                  ariaLabel={`Override weight for ${comp}`}
                />
              </Grid>
              <Grid item xs={1}>
                <Stack direction="row" spacing={0.75} justifyContent="flex-end">
                  <IconButton
                    size="small"
                    title={canSave ? "Save override" : "No changes to save"}
                    aria-label="Save override"
                    sx={actionBtnSx(canSave)}
                    onClick={() => onSaveOne(comp)}
                    disabled={!canSave}
                  >
                    <LayerdIcon icon={Check} size="sm" color={canSave ? "primary" : "neutral"} />
                  </IconButton>
                  <IconButton
                    size="small"
                    title={canReset ? "Reset to default" : "Nothing to reset"}
                    aria-label="Reset override"
                    sx={actionBtnSx(canReset)}
                    onClick={() => onResetOne(comp)}
                    disabled={!canReset}
                  >
                    <LayerdIcon icon={RotateCcw} size="sm" color={canReset ? "primary" : "neutral"} />
                  </IconButton>
                </Stack>
              </Grid>
            </Grid>
          );
        })}
        <Typography sx={{ px: 2.25, py: 1.375, borderTop: `1px solid ${colors.neutral[200]}`, fontSize: 12, color: colors.neutral[500] }}>
          {componentList.length} signals · default sum caps at 100 · {Object.keys(overrides).length} override{Object.keys(overrides).length === 1 ? "" : "s"} active.
        </Typography>
      </AsecSettingsTableCard>
    </>
  );
}

function RoutesTab({ rows, onAdd, onEdit, onDelete, canWrite, canDelete, search, setSearch }) {
  const filtered = rows.filter((r) => !search
    || (r.path_template || "").toLowerCase().includes(search.toLowerCase())
    || (r.method || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1.25} flexWrap="wrap" sx={{ mb: 1.75 }}>
        <SafeTextField
          size="small"
          placeholder="Search route…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ width: 300 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <LayerdIcon icon={Search} size="sm" color="neutral" />
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ ml: "auto" }}>
          <PrimaryButton startIcon={<LayerdIcon icon={Plus} size="sm" color="inherit" />} onClick={onAdd} disabled={!canWrite}>
            Add override
          </PrimaryButton>
        </Box>
      </Stack>

      <AsecSettingsTableCard>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: colors.neutral[50] }}>
                <TableCell sx={asecSettingsTableHeadSx} width={74}>Method</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Path template</TableCell>
                <TableCell sx={asecSettingsTableHeadSx}>Thresholds</TableCell>
                <TableCell sx={asecSettingsTableHeadSx} align="right">Scored · 24h</TableCell>
                <TableCell sx={asecSettingsTableHeadSx} align="right">Would deny</TableCell>
                <TableCell sx={asecSettingsTableHeadSx} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 5, color: colors.neutral[700] }}>
                    {rows.length === 0
                      ? <>No route overrides. Click <strong>Add override</strong> to tighten or loosen thresholds for specific routes.</>
                      : "No routes match this filter."}
                  </TableCell>
                </TableRow>
              )}
              {filtered.map((r) => (
                <TableRow key={r.id} hover>
                  <TableCell sx={asecSettingsTableBodySx}><MethodTag m={r.method || "ANY"} /></TableCell>
                  <TableCell sx={asecSettingsTableBodySx}><PathCell template={r.path_template} /></TableCell>
                  <TableCell sx={asecSettingsTableBodySx}>
                    <Typography sx={{ fontSize: 12 }}>
                      challenge <strong style={{ color: "#7a5b10" }}>{r.challenge_threshold}</strong>
                      {" · "}deny <strong style={{ color: colors.red[200] }}>{r.deny_threshold}</strong>
                    </Typography>
                  </TableCell>
                  <TableCell sx={asecSettingsTableBodySx} align="right">
                    {r.scored_24h != null ? Number(r.scored_24h).toLocaleString() : "—"}
                  </TableCell>
                  <TableCell sx={asecSettingsTableBodySx} align="right">
                    {r.would_deny_24h != null
                      ? (r.would_deny_24h > 0
                          ? <Box component="span" sx={{ color: colors.red[200], fontWeight: 600 }}>{Number(r.would_deny_24h).toLocaleString()}</Box>
                          : "0")
                      : "—"}
                  </TableCell>
                  <TableCell sx={asecSettingsTableBodySx} align="right">
                    <Stack direction="row" spacing={0.25} justifyContent="flex-end">
                      <IconButton size="small" title="Edit" sx={asecIconButtonSx} onClick={() => onEdit(r)} disabled={!canWrite}>
                        <LayerdIcon icon={Pencil} size="sm" color="neutral" />
                      </IconButton>
                      <IconButton size="small" title="Remove" sx={{ ...asecIconButtonSx, "&:hover": { color: colors.red[200], backgroundColor: colors.red[10] } }}
                        onClick={() => onDelete(r)} disabled={!canDelete}>
                        <LayerdIcon icon={Trash2} size="sm" color="neutral" />
                      </IconButton>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <Typography sx={{ px: 2.25, py: 1.375, borderTop: `1px solid ${colors.neutral[200]}`, fontSize: 12, color: colors.neutral[500] }}>
          Routes not listed use the global thresholds. More specific path matches win.
        </Typography>
      </AsecSettingsTableCard>
    </>
  );
}

const routeDrawerMenuItemSx = {
  height: 38,
  fontSize: 14,
  lineHeight: "21px",
  "&:hover": { color: colors.primary[300] },
};

function RouteDrawer({ open, draft, setDraft, onClose, onSave, onDelete, saving, isNew, canDelete }) {
  if (!draft) return null;

  const canSave = Number(draft.deny_threshold) >= Number(draft.challenge_threshold);
  const titleId = "route-override-drawer-title";
  const subtitleId = "route-override-drawer-subtitle";

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onClose}
      aria-labelledby={titleId}
      aria-describedby={subtitleId}
      slotProps={{
        backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.5)" } },
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          tabIndex={-1}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "33%",
            minWidth: 480,
            maxWidth: "94vw",
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 20px rgba(0,0,0,0.1)",
            zIndex: 1300,
            borderTopLeftRadius: "8px",
            outline: "none",
          }}
        >
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
            spacing={2}
            sx={{ px: 4, pt: 4, pb: 3 }}
          >
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <LayerdIcon icon={Route} size={32} color="#6371F7" />
              </Box>
              <Stack sx={{ minWidth: 0 }}>
                <Stack direction="row" alignItems="center" sx={{ flexWrap: "nowrap", minWidth: 0 }}>
                  <Typography
                    id={titleId}
                    sx={{
                      fontWeight: "medium",
                      fontSize: 20,
                      lineHeight: "30px",
                      color: colors.neutral[1000],
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {isNew ? "New route override" : "Edit route override"}
                  </Typography>
                  <FormHelpTooltip tooltip="Override global risk-score challenge and deny thresholds for a specific method and path template. Matched routes use these limits instead of site defaults." />
                </Stack>
                <Typography id={subtitleId} sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700] }}>
                  Routes matched by this pattern use these thresholds instead of the global ones.
                </Typography>
              </Stack>
            </Stack>
            <IconButton onClick={onClose} disabled={saving} aria-label="Close" sx={{ p: 0, flexShrink: 0 }}>
              <CloseIcon style={{ color: colors.neutral[600] }} />
            </IconButton>
          </Stack>

          <Box sx={{ py: 4, pl: 4, height: "calc(100vh - 130px)" }}>
            <ReactPerfectScrollbar>
              <Stack justifyContent="space-between" spacing={4} sx={{ pr: 4, height: "100%" }}>
                <Stack spacing={4}>
                  <GreenSelectForm variant="outlined" fullWidth disabled={!isNew}>
                    <InputLabel id="route-override-method-label">Method</InputLabel>
                    <Select
                      fullWidth
                      labelId="route-override-method-label"
                      label="Method"
                      IconComponent={ArrowdownIcon}
                      value={draft.method || "ANY"}
                      disabled={!isNew}
                      onChange={(e) => setDraft({ ...draft, method: e.target.value })}
                      MenuProps={{ disablePortal: true }}
                    >
                      {ROUTE_METHODS.map((m) => (
                        <MenuItem key={m} value={m} sx={routeDrawerMenuItemSx}>{m}</MenuItem>
                      ))}
                    </Select>
                  </GreenSelectForm>

                  <SafeTextField
                    fullWidth
                    label="Path template"
                    disabled={!isNew}
                    value={draft.path_template || ""}
                    onChange={(e) => setDraft({ ...draft, path_template: e.target.value })}
                    helperText="OpenAPI-style. Parameters in {curly braces}."
                  />

                  <Box>
                    <Typography
                      id="route-override-inline-label"
                      sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[1000], mb: 1 }}
                    >
                      Inline enforcement
                    </Typography>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{
                        p: 2,
                        border: `1px solid ${colors.neutral[200]}`,
                        borderRadius: "8px",
                        backgroundColor: "#fff",
                      }}
                    >
                      <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[1000] }}>
                        Apply this override inline
                      </Typography>
                      <GreenSwitch
                        checked={!!draft.inline_enforce}
                        onChange={(e) => setDraft({ ...draft, inline_enforce: e.target.checked })}
                        inputProps={{ "aria-labelledby": "route-override-inline-label" }}
                      />
                    </Stack>
                    <Typography sx={{ fontSize: 12, lineHeight: "18px", color: colors.neutral[700], mt: 1 }}>
                      When on, this route uses inline 403/429 even if the global toggle is off.
                    </Typography>
                  </Box>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <AsecField label="Challenge threshold" helper={<>Score ≥ this returns <AsecMono>429</AsecMono>.</>}>
                        <ThresholdStepper
                          value={draft.challenge_threshold}
                          onChange={(v) => setDraft({ ...draft, challenge_threshold: v })}
                        />
                      </AsecField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <AsecField label="Deny threshold" helper={<>Score ≥ this returns <AsecMono>403</AsecMono>.</>}>
                        <ThresholdStepper
                          value={draft.deny_threshold}
                          onChange={(v) => setDraft({ ...draft, deny_threshold: v })}
                        />
                      </AsecField>
                    </Grid>
                  </Grid>

                  {!canSave && (
                    <Alert severity="error" sx={{ fontSize: 12 }}>
                      Deny threshold must be ≥ challenge threshold.
                    </Alert>
                  )}

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{
                      minHeight: 48,
                      p: 2,
                      borderRadius: "8px",
                      backgroundColor: colors.primary.alpha,
                      border: `solid 1px ${colors.primary[300]}`,
                    }}
                  >
                    <Box sx={{ width: 24, height: 24, flexShrink: 0 }}>
                      <InfoIcon style={{ color: colors.primary[300], width: 24, height: 24 }} />
                    </Box>
                    <Typography sx={{ fontSize: 14, lineHeight: "21px", color: colors.primary[300] }}>
                      Matched routes use these thresholds instead of site defaults. Changes apply as soon as you save.
                    </Typography>
                  </Stack>
                </Stack>

                <Stack spacing={2}>
                  {!isNew && canDelete && (
                    <GhostButton
                      startIcon={<LayerdIcon icon={Trash2} size="sm" color="neutral" />}
                      onClick={onDelete}
                      disabled={saving}
                      sx={{ color: colors.red[200], borderColor: "#FBC9CF", alignSelf: "flex-start" }}
                    >
                      Remove
                    </GhostButton>
                  )}
                  <Stack direction="row" alignItems="center" spacing={3}>
                    <SaveButton
                      onClick={onSave}
                      disabled={saving || !canSave}
                      loading={saving}
                      label="Apply"
                      sx={formSaveButtonSx}
                    />
                    <CancelButton
                      onClick={onClose}
                      disabled={saving}
                      sx={formCancelButtonSx}
                    />
                  </Stack>
                </Stack>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>
        </Stack>
      </Slide>
    </Modal>
  );
}

function InvestigateBreakdown({ result }) {
  const ev = result.data.event;
  const comps = result.data.components || [];
  const ctx = result.data.recent_context || [];
  const score = Number(ev.risk_score || 0);
  const verdict = ev.risk_verdict || "—";
  const verdictColor = verdict === "deny" ? colors.red[200] : verdict === "challenge" ? "#7a5b10" : colors.green[300];
  const ringColor = verdict === "deny" ? colors.red[200] : verdict === "challenge" ? colors.yellow[100] : colors.green[300];
  const compsSum = comps.reduce((a, c) => a + Number(c.points || 0), 0);
  const capped = Math.max(0, compsSum - score);
  const maxPts = Math.max(...comps.map((c) => Number(c.points || 0)), 1);
  const circ = 2 * Math.PI * 15.5;

  return (
    <Grid container sx={{ borderTop: `1px solid ${colors.neutral[200]}` }}>
      <Grid item xs={12} md={6} sx={{ p: 2.25, borderRight: { md: `1px solid ${colors.neutral[200]}` } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.75 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700] }}>
            Score breakdown
          </Typography>
          <Verdict v={verdict} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2.25 }}>
          <svg width="78" height="78" viewBox="0 0 36 36">
            <circle cx="18" cy="18" r="15.5" fill="none" stroke={colors.neutral[50]} strokeWidth="4" />
            <circle cx="18" cy="18" r="15.5" fill="none" stroke={ringColor} strokeWidth="4"
              strokeLinecap="round" strokeDasharray={circ.toFixed(2)}
              strokeDashoffset={(circ * (1 - score / 100)).toFixed(2)}
              transform="rotate(-90 18 18)" />
          </svg>
          <Box>
            <Typography sx={{ fontSize: 34, fontWeight: 800, color: verdictColor, lineHeight: 1 }}>{score}</Typography>
            <Typography sx={{ fontSize: 12, color: colors.neutral[700], mt: 0.5 }}>
              fused score · {verdict}
            </Typography>
          </Box>
        </Stack>
        {comps.length === 0 && (
          <Typography sx={{ fontSize: 14, color: colors.neutral[700], py: 1 }}>
            No components fired — this request scored 0. If the user still saw a block, it likely came from a module that runs before the K scorer (BOLA, WAF rules, RL, BM block).
          </Typography>
        )}
        {comps.map((c) => (
          <Stack key={c.label} direction="row" alignItems="center" spacing={1.25} sx={{ py: 1, borderBottom: `1px solid rgba(0,0,0,0.08)` }}>
            <Typography sx={{ fontSize: 12, width: 140, flexShrink: 0 }}>{c.label}</Typography>
            <LinearProgress variant="determinate" value={(Number(c.points || 0) / maxPts) * 100} sx={{
              flex: 1, height: 8, borderRadius: "4px", backgroundColor: colors.neutral[50],
              "& .MuiLinearProgress-bar": { borderRadius: "4px", background: `linear-gradient(90deg,${colors.red[100]},${colors.red[200]})` },
            }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, width: 38, textAlign: "right" }}>{c.points ?? "—"}</Typography>
          </Stack>
        ))}
        {capped > 0 && (
          <Stack direction="row" alignItems="center" spacing={1.25} sx={{ py: 1, color: colors.neutral[500] }}>
            <Typography sx={{ fontSize: 12, width: 140, flexShrink: 0 }}>capped at 100</Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ fontSize: 12, fontWeight: 700, width: 38, textAlign: "right" }}>−{capped}</Typography>
          </Stack>
        )}
        {result.data.weights_source && (
          <Typography sx={{ mt: 1.25, fontSize: 12, color: colors.neutral[500] }}>
            weights source:{" "}
            <Box component="span" sx={{ color: result.data.weights_source === "pingora" ? colors.green[300] : "#7a5b10", fontWeight: 600 }}>
              {result.data.weights_source}
            </Box>
          </Typography>
        )}
      </Grid>

      <Grid item xs={12} md={6} sx={{ p: 2.25 }}>
        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700], mb: 1.75 }}>
          Request &amp; caller
        </Typography>
        <Grid container spacing={1} sx={{ fontSize: 12, mb: 2 }}>
          <Kv k="Request ID" v={<AsecMono>{ev.request_id || "—"}</AsecMono>} />
          <Kv k="Route" v={<AsecMono>{ev.method} {ev.matched_path_template || ev.raw_path}</AsecMono>} />
          <Kv k="Status" v={<><AsecMono>{ev.response_status}</AsecMono> {ev.response_status >= 400 && "problem+json"}</>} />
          <Kv k="Caller IP" v={<AsecMono>{ev.src_ip || "—"}</AsecMono>} />
          <Kv k="Country" v={<AsecMono>{ev.src_country || "—"}</AsecMono>} />
          <Kv k="Caller class" v={<AsecMono>{ev.caller_class || "—"}</AsecMono>} />
          <Kv k="Time" v={ev.ts || "—"} />
          {ev.dlp_max_tier && <Kv k="DLP tier" v={<AsecMono>{ev.dlp_max_tier}</AsecMono>} />}
          {ev.ml_anomaly_score != null && <Kv k="ML score" v={<AsecMono>{ev.ml_anomaly_score}</AsecMono>} />}
          {ev.prompt_injection_confidence != null && Number(ev.prompt_injection_confidence) > 0
            && <Kv k="Prompt injection" v={<AsecMono>{ev.prompt_injection_confidence}</AsecMono>} />}
        </Grid>

        <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: colors.neutral[700], mb: 1 }}>
          Recent activity · {ev.src_ip || "this IP"}
        </Typography>
        {ctx.length === 0 && (
          <Typography sx={{ fontSize: 12, color: colors.neutral[700], py: 1 }}>No other requests from this IP in the last hour.</Typography>
        )}
        {ctx.slice(0, 12).map((r, i) => (
          <Stack key={i} direction="row" alignItems="center" spacing={1.25}
            sx={{ py: 0.875, borderBottom: `1px solid rgba(0,0,0,0.08)`, fontSize: 12 }}>
            <Typography sx={{ fontSize: 12, color: colors.neutral[500], width: 64, flexShrink: 0 }}>
              {(r.ts || "").slice(11, 19) || (r.ts || "—")}
            </Typography>
            <Verdict v={r.risk_verdict || "allow"} sm />
            <Typography sx={{ fontSize: 12, color: colors.neutral[700], overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {r.method} {r.raw_path} · {r.risk_score}
            </Typography>
          </Stack>
        ))}
      </Grid>
    </Grid>
  );
}

function Kv({ k, v }) {
  return (
    <>
      <Grid item xs={4}>
        <Typography sx={{ color: colors.neutral[700], fontWeight: 700, fontSize: 12, textTransform: "uppercase", letterSpacing: "0.04em" }}>{k}</Typography>
      </Grid>
      <Grid item xs={8}><Typography sx={{ fontSize: 12 }}>{v}</Typography></Grid>
    </>
  );
}

function InvestigateTab({ requestId, setRequestId, onLookup, loading, result, onClearResult }) {
  const lookupFailed = !loading && result && !result.ok;
  const errorMessage = result?.notFound
    ? "No matching request Id in the last 7 days. Check the id (case-sensitive) or try a more recent one."
    : (result?.message || "Something went wrong looking up that request.");

  return (
    <AsecSectionCard title="Why was this request blocked?" icon={SearchCheck} radius="12px" noPadding
      right={<Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>retained 7 days</Typography>}
      bodySx={{ p: 0 }}>
      <Stack spacing={1.25} sx={{ p: 2.25 }}>
        <Stack direction="row" spacing={1.25} alignItems="flex-end">
          <AsecTextField fullWidth label="Request ID"
            value={requestId}
            placeholder="req_8f3a2c91e740bb02"
            onChange={(e) => {
              setRequestId(e.target.value);
              if (result && !result.ok) onClearResult?.();
            }}
            onKeyDown={(e) => { if (e.key === "Enter" && !loading) onLookup(); }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LayerdIcon icon={Hash} size="sm" color="neutral" />
                </InputAdornment>
              ),
              style: { fontSize: 14 },
            }}
          />
          <PrimaryButton
            startIcon={<LayerdIcon icon={Search} size="sm" color="inherit" />}
            disabled={loading || !requestId.trim()}
            onClick={onLookup}
            sx={{ ...asecFormPrimaryButtonSx, width: 132, minWidth: 132, flexShrink: 0 }}
          >
            {loading ? "Looking up…" : "Look up"}
          </PrimaryButton>
        </Stack>
        <Typography sx={{ fontSize: 12, color: colors.neutral[600], ...asecHelperTextSx }}>
          Paste a request Id from a 403 / 429 the caller received — on the problem body&apos;s <AsecMono>instance</AsecMono> path, the <AsecMono>X-Layerd-Request-Id</AsecMono> header, or in logs for the last 7 days.
        </Typography>

        {lookupFailed && (
          <Box
            role="alert"
            sx={{
              position: "relative",
              display: "flex",
              alignItems: "center",
              gap: 1.25,
              px: "16px",
              py: "12px",
              borderRadius: "8px",
              border: "1px solid rgba(251, 55, 72, 0.28)",
              background: "linear-gradient(135deg, rgba(251,55,72,0.10) 0%, rgba(251,55,72,0.03) 100%)",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 3,
                zIndex: 0,
                backgroundColor: colors.red[100],
              },
            }}
          >
            <LayerdIcon icon={ShieldAlert} size="sm" color={colors.red[100]} />
            <Typography sx={{ fontSize: 12, color: colors.neutral[700], lineHeight: "18px", position: "relative", zIndex: 1, minWidth: 0 }}>
              {errorMessage}
            </Typography>
          </Box>
        )}
      </Stack>

      {!loading && result && result.ok && result.data && result.data.event && (
        <InvestigateBreakdown result={result} />
      )}
    </AsecSectionCard>
  );
}

function Body({ siteId, isEnvDefault, setIsEnvDefault }) {
  const { showSuccess, showError, showInfo } = useNotification();
  const { canConfigure, canWrite, canDelete } = useAsecPermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = React.useState(() => resolveRiskScoreTab(searchParams.get("tab")));
  const [baseline, setBaseline] = React.useState(null);
  const [form, setForm] = React.useState(null);
  const [saving, setSaving] = React.useState(false);
  const [dist, setDist] = React.useState([]);
  const [defaults, setDefaults] = React.useState({});
  const [overrides, setOverrides] = React.useState({});
  const [drafts, setDrafts] = React.useState({});
  const [routes, setRoutes] = React.useState([]);
  const [routeDrawer, setRouteDrawer] = React.useState(null);
  const [routeSaving, setRouteSaving] = React.useState(false);
  const [pendingDeleteRoute, setPendingDeleteRoute] = React.useState(null);
  const [deletingRoute, setDeletingRoute] = React.useState(false);
  const [weightSearch, setWeightSearch] = React.useState("");
  const [routeSearch, setRouteSearch] = React.useState("");
  const [requestId, setRequestId] = React.useState("");
  const [lookupLoading, setLookupLoading] = React.useState(false);
  const [lookupResult, setLookupResult] = React.useState(null);

  React.useEffect(() => {
    const raw = searchParams.get("tab");
    const resolved = resolveRiskScoreTab(raw);
    setTab(resolved);

    // Canonical URL: omit default; drop empty/invalid/?tab=thresholds.
    // RR 6.3 setSearchParams does not accept an updater fn — pass URLSearchParams.
    const trimmed = raw == null ? null : String(raw).trim();
    const shouldOmitTabParam =
      searchParams.has("tab")
      && (trimmed === "" || trimmed === DEFAULT_TAB || trimmed !== resolved);

    if (!shouldOmitTabParam) return;

    const next = new URLSearchParams(searchParams);
    next.delete("tab");
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleTabChange = React.useCallback((_, idx) => {
    const nextTab = TAB_KEYS[idx] || DEFAULT_TAB;
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    if (nextTab === DEFAULT_TAB) {
      next.delete("tab");
    } else {
      next.set("tab", nextTab);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const reload = React.useCallback(async () => {
    if (!siteId) return;
    try {
      const [cfgR, distR, ovrR, routesR] = await Promise.all([
        asec.riskConfigGet(siteId),
        asec.riskConfigDistribution(siteId, { hours: 24 }).catch(() => ({ data: { items: [] } })),
        asec.riskWeightOverridesList(siteId).catch(() => ({ data: { defaults: {}, items: [] } })),
        asec.riskConfigRoutesList(siteId).catch(() => ({ data: { items: [] } })),
      ]);
      const cfg = cfgR.data || {};
      const next = {
        inline_enforce: !!cfg.inline_enforce,
        challenge_threshold: Number(cfg.challenge_threshold ?? 80),
        deny_threshold: Number(cfg.deny_threshold ?? 95),
        challenge_response_format: cfg.challenge_response_format || "auto",
      };
      setBaseline(next);
      setForm(next);
      setIsEnvDefault(!!cfg.is_env_default);
      setDist(distR.data?.items || []);
      setDefaults(ovrR.data?.defaults || {});
      const ovr = {};
      for (const r of ovrR.data?.items || []) ovr[r.component] = r.points;
      setOverrides(ovr);
      setDrafts({});
      setRoutes(routesR.data?.items || []);
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    }
  }, [siteId, showError, setIsEnvDefault]);
  React.useEffect(() => { reload(); }, [reload]);

  const modifiedFields = React.useMemo(
    () => collectObjectDiff(baseline, form, RISK_POLICY_DIFF_FIELDS),
    [baseline, form],
  );
  const modifiedCount = modifiedFields.length;

  const patch = (p) => {
    if (!canConfigure) return;
    setForm((prev) => ({ ...prev, ...p }));
  };

  const discard = () => {
    if (!baseline) return;
    setForm(baseline);
  };

  const save = async () => {
    if (!form || !canConfigure) return;
    if (form.deny_threshold < form.challenge_threshold) {
      showError("Deny threshold must be ≥ challenge threshold.");
      return;
    }
    setSaving(true);
    try {
      await asec.riskConfigUpsert(siteId, form);
      showSuccess("Risk config saved. Pingora reloads in ~1s.");
      setIsEnvDefault(false);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetEnvDefaults = async () => {
    if (!canConfigure) return;
    setSaving(true);
    try {
      await asec.riskConfigDelete(siteId);
      showSuccess("Reverted to env defaults.");
      await reload();
    } catch (e) {
      if (e?.response?.status === 404) {
        showInfo("Already using env defaults.");
      } else {
        showError(e?.response?.data?.message || e.message);
      }
    } finally {
      setSaving(false);
    }
  };

  const setDraft = (comp, v) => setDrafts((d) => ({ ...d, [comp]: v }));
  const saveWeight = async (comp) => {
    const v = drafts[comp];
    if (v === undefined || v === "" || isNaN(Number(v))) return;
    const n = Math.max(0, Math.min(100, Math.round(Number(v))));
    try {
      await asec.riskWeightOverrideSet(siteId, comp, n);
      showSuccess(`${comp} now overridden to ${n} (default ${defaults[comp]}).`);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    }
  };
  const resetWeight = async (comp) => {
    try {
      await asec.riskWeightOverrideDelete(siteId, comp);
      showSuccess(`${comp} reverted to pingora default (${defaults[comp]}).`);
      await reload();
    } catch (e) {
      if (e?.response?.status === 404) {
        showInfo(`${comp} already using pingora default.`);
      } else {
        showError(e?.response?.data?.message || e.message);
      }
    }
  };
  const resetAllWeights = async () => {
    if (!window.confirm("Reset ALL weight overrides for this site?")) return;
    for (const comp of Object.keys(overrides)) {
      try { await asec.riskWeightOverrideDelete(siteId, comp); }
      catch (_) { /* swallow */ }
    }
    showSuccess("All weight overrides reset.");
    await reload();
  };

  const openRouteNew = () => {
    if (!canWrite) return;
    setRouteDrawer({ mode: "new", row: null, draft: emptyRoute() });
  };
  const openRouteEdit = (row) => {
    setRouteDrawer({
      mode: "edit", row,
      draft: {
        id: row.id,
        method: row.method,
        path_template: row.path_template,
        inline_enforce: !!row.inline_enforce,
        challenge_threshold: Number(row.challenge_threshold),
        deny_threshold: Number(row.deny_threshold),
      },
    });
  };
  const closeRouteDrawer = () => setRouteDrawer(null);
  const setRouteDraft = (next) => setRouteDrawer((d) => (d ? { ...d, draft: next } : d));

  const saveRoute = async () => {
    if (!routeDrawer) return;
    const d = routeDrawer.draft;
    if (d.deny_threshold < d.challenge_threshold) {
      showError("Deny threshold must be ≥ challenge threshold.");
      return;
    }
    setRouteSaving(true);
    try {
      if (routeDrawer.mode === "edit") {
        await asec.riskConfigRoutePatch(siteId, d.id, {
          inline_enforce: d.inline_enforce,
          challenge_threshold: d.challenge_threshold,
          deny_threshold: d.deny_threshold,
        });
      } else {
        await asec.riskConfigRouteCreate(siteId, {
          method: d.method, path_template: d.path_template,
          inline_enforce: d.inline_enforce,
          challenge_threshold: d.challenge_threshold,
          deny_threshold: d.deny_threshold,
        });
      }
      showSuccess("Route override saved.");
      closeRouteDrawer();
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setRouteSaving(false);
    }
  };

  const requestDeleteRoute = (row) => {
    if (!row || !canDelete) return;
    setPendingDeleteRoute(row);
  };

  const closeDeleteRouteModal = () => {
    if (deletingRoute) return;
    setPendingDeleteRoute(null);
  };

  const confirmDeleteRoute = async () => {
    if (!pendingDeleteRoute || !canDelete || deletingRoute) return;
    setDeletingRoute(true);
    try {
      await asec.riskConfigRouteDelete(siteId, pendingDeleteRoute.id);
      showSuccess("Route override removed.");
      if (routeDrawer && routeDrawer.row?.id === pendingDeleteRoute.id) closeRouteDrawer();
      setPendingDeleteRoute(null);
      await reload();
    } catch (e) {
      showError(e?.response?.data?.message || e.message);
    } finally {
      setDeletingRoute(false);
    }
  };

  const deleteRouteFromDrawer = () => routeDrawer?.row && requestDeleteRoute(routeDrawer.row);

  const lookup = async () => {
    const id = (requestId || "").trim();
    if (!id) return;
    setLookupLoading(true);
    setLookupResult(null);
    try {
      const { data } = await asec.riskConfigLookup(siteId, id);
      setLookupResult({ ok: true, data });
    } catch (e) {
      // userAxios rejects with response.data (HTTP status stripped) — detect 404 via message.
      const msg =
        (typeof e === "string" ? e : null) ||
        e?.message ||
        e?.response?.data?.message ||
        "Lookup failed.";
      const notFound = /no api-observations row found/i.test(String(msg));
      setLookupResult({ ok: false, message: msg, notFound });
    } finally {
      setLookupLoading(false);
    }
  };

  if (!form) {
    return (
      <Typography sx={{ p: 5, textAlign: "center", color: colors.neutral[700] }}>
        Loading risk config…
      </Typography>
    );
  }

  const tabIndex = TAB_KEYS.indexOf(tab);

  return (
    <Stack spacing={2.5}>
      {isEnvDefault && (
        <AsecStatusPill kind="default">
          <Stack direction="row" alignItems="center" spacing={0.75} component="span">
            <LayerdIcon icon={GitBranch} size="sm" color="neutral" />
            Using env defaults
          </Stack>
        </AsecStatusPill>
      )}

      <HeroMetrics form={form} dist={dist} />

      <Tabs value={tabIndex >= 0 ? tabIndex : 0} onChange={handleTabChange} sx={asecTabsSx}>
        <Tab label="Thresholds & verdicts" />
        <Tab label={`Signal weights (${Object.keys(defaults).length})`} />
        <Tab label={`Route overrides (${routes.length})`} />
        <Tab label="Investigate" />
      </Tabs>

      <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={1} sx={{ mt: -1 }}>
        <Box sx={{ width: 7, height: 7, borderRadius: "50%", backgroundColor: colors.green[200], flexShrink: 0 }} />
        <Typography sx={{ fontSize: 12, color: colors.neutral[700] }}>Live · last sync 4s ago</Typography>
        {tab === "thresholds" && (
          <GhostButton
            size="small"
            startIcon={<LayerdIcon icon={RotateCcw} size="sm" color="neutral" />}
            onClick={resetEnvDefaults}
            disabled={!canConfigure || isEnvDefault || saving}
            title={
              !canConfigure
                ? "You do not have permission to reset"
                : isEnvDefault
                  ? "Already using env defaults"
                  : "Reset thresholds policy to env defaults"
            }
          >
            Reset to env defaults
          </GhostButton>
        )}
      </Stack>

      {tab === "thresholds" && (
        <ThresholdsTab form={form} patch={patch} dist={dist} canConfigure={canConfigure} />
      )}
      {tab === "weights" && (
        <WeightsTab defaults={defaults} overrides={overrides} drafts={drafts}
          setDraft={setDraft} onSaveOne={saveWeight} onResetOne={resetWeight}
          onResetAll={resetAllWeights} canConfigure={canConfigure}
          search={weightSearch} setSearch={setWeightSearch} />
      )}
      {tab === "routes" && (
        <RoutesTab rows={routes} onAdd={openRouteNew} onEdit={openRouteEdit} onDelete={requestDeleteRoute}
          canWrite={canWrite} canDelete={canDelete} search={routeSearch} setSearch={setRouteSearch} />
      )}
      {tab === "investigate" && (
        <InvestigateTab requestId={requestId} setRequestId={setRequestId}
          onLookup={lookup} loading={lookupLoading} result={lookupResult}
          onClearResult={() => setLookupResult(null)} />
      )}

      {tab === "thresholds" && (
        <StickySaveBar
          open={modifiedCount > 0 && canConfigure}
          modifiedCount={modifiedCount}
          modifiedFields={modifiedFields}
          onDiscard={discard}
          onApply={save}
          isApplying={saving}
        />
      )}

      <RouteDrawer
        open={!!routeDrawer}
        draft={routeDrawer?.draft}
        setDraft={setRouteDraft}
        isNew={routeDrawer?.mode === "new"}
        canDelete={canDelete}
        onClose={closeRouteDrawer}
        onSave={saveRoute}
        onDelete={deleteRouteFromDrawer}
        saving={routeSaving}
      />

      <Modal
        open={Boolean(pendingDeleteRoute)}
        onClose={deletingRoute ? undefined : closeDeleteRouteModal}
        aria-labelledby="delete-route-override-title"
        aria-describedby="delete-route-override-desc"
        sx={{ zIndex: 1400 }}
      >
        <Stack
          tabIndex={-1}
          spacing={4}
          sx={{
            position: "absolute",
            top: "30%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 450,
            maxWidth: "94vw",
            backgroundColor: colors.neutral[100],
            borderRadius: "8px",
            p: 6,
            outline: "none",
          }}
        >
          <Stack spacing={3} direction="row" alignItems="center" justifyContent="space-between">
            <Typography
              id="delete-route-override-title"
              sx={{ fontSize: 20, lineHeight: "30px", fontWeight: "medium", color: colors.neutral[1000] }}
            >
              Delete route override?
            </Typography>
            <IconButton
              onClick={closeDeleteRouteModal}
              disabled={deletingRoute}
              aria-label="Close"
              sx={{ p: 0 }}
            >
              <LayerdIcon icon={X} size={20} color="neutral" />
            </IconButton>
          </Stack>
          <Typography
            id="delete-route-override-desc"
            sx={{ fontSize: 14, lineHeight: "21px", color: colors.neutral[700], wordBreak: "break-word" }}
          >
            Are you sure you want to permanently delete the override for{" "}
            <strong>{pendingDeleteRoute?.method} {pendingDeleteRoute?.path_template}</strong>?
            This action cannot be undone.
          </Typography>
          <Stack direction="row" alignItems="center" spacing={4}>
            <CancelButton onClick={closeDeleteRouteModal} outlined disabled={deletingRoute} />
            <SaveButton
              onClick={confirmDeleteRoute}
              disabled={deletingRoute || !canDelete}
              loading={deletingRoute}
              label="Delete"
            />
          </Stack>
        </Stack>
      </Modal>
    </Stack>
  );
}

export default function RiskScore() {
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [isEnvDefault, setIsEnvDefault] = React.useState(true);
  return (
    <AsecPage
      featureKey="api.auth_primitives"
      helpTooltip="Per-site risk-score config — fused 0–100 verdict per request from auth + identity behaviour + DLP + ML + bot + geo + TLS + inline anomaly + signatures. Above challenge_threshold → 429; above deny_threshold → 403. Inline enforce gates whether the proxy actually returns those statuses or only records the verdict in CH."
      title="Risk Score"
      helmetTitle="API Security — Risk Score"
      pageDescription={(
        <>
          The fused per-request risk score (0–100) combines auth, identity behaviour, sensitive-data exposure, ML, bot, geo, TLS and signature signals.
          At or above <strong>challenge</strong> trips a 429; at or above <strong>deny</strong> trips a 403.
          Inline enforcement is gated separately so you can tune from observed traffic first.
        </>
      )}
      onRefresh={() => setRefreshKey((k) => k + 1)}
    >
      {(siteId) => (
        <Body
          key={refreshKey}
          siteId={siteId}
          isEnvDefault={isEnvDefault}
          setIsEnvDefault={setIsEnvDefault}
        />
      )}
    </AsecPage>
  );
}
